import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Spin, Row, Col, Input, InputNumber, message } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Box, Flex, Text, Button } from 'atoms';
import { AntdTable } from 'components/custom-table/Style';
import { CancelButton } from 'utils/commonStyled';
import {
	getClientInvoiceDetailV2,
	editRegeneratedClientInvoiceV2,
} from 'store/clientInvoiceSliceV2';

const { TextArea } = Input;

const LIST_ROUTE = '/client-invoices';
const GST_RATE = 0.18;

const Card = styled(Box)`
	background: #fff;
	border-radius: 8px;
	border-top: 2px solid #2f8ecb;
	margin-bottom: 16px;
	padding: 24px;
`;
const SubTitle = styled(Text)`
	font-size: 18px;
	font-weight: 600;
	display: block;
	margin-bottom: 16px;
`;
const RowLine = styled(Row)`
	padding: 8px 0;
	align-items: center;
`;
const GreyLine = styled(RowLine)`
	background: #f1f3f5;
	margin: 4px 0;
	padding: 10px 8px;
`;

const formatINR = (value) => {
	const n = Number(value);
	if (value == null || Number.isNaN(n)) return '₹0.00';
	return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const TRIP_COLUMNS = [
	{ title: 'Trip ID', dataIndex: 'trip_id', key: 'trip_id', render: (t) => t || '-' },
	{ title: 'Driver', dataIndex: 'driver', key: 'driver', render: (t) => t || '-' },
	{ title: 'Route', dataIndex: 'route', key: 'route', render: (t) => t || '-' },
	{ title: 'Vehicle', dataIndex: 'vehicle', key: 'vehicle', render: (t) => t || '-' },
	{ title: 'Date', dataIndex: 'date', key: 'date', render: (t) => t || '-' },
	{ title: 'Amount', dataIndex: 'amount', key: 'amount', render: (t) => formatINR(t) },
];

/**
 * Client Invoice bill-edit screen (regeneration step 2) — AR mirror of
 * VendorBillEditForm. Shows invoice details + the chosen Regular / Adhoc trips,
 * then a Total Payable section where TDS / Customer Debit can be adjusted. No
 * document is required at this step (the whole regeneration is approved as one).
 */
function EditClientInvoicePageV2() {
	const { id } = useParams();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [invoice, setInvoice] = useState(null);
	const [trips, setTrips] = useState({ regular: [], adhoc: [] });
	const [tds, setTds] = useState(0);
	const [debit, setDebit] = useState(0);
	const [remarks, setRemarks] = useState('');

	useEffect(() => {
		setLoading(true);
		dispatch(getClientInvoiceDetailV2({ id }))
			.then((res) => {
				const inv = res?.invoice || null;
				setInvoice(inv);
				setTrips(res?.trips || { regular: [], adhoc: [] });
				setTds(Number(inv?.tds_amount || 0));
				setDebit(Number(inv?.customer_debit_amount || 0));
				setRemarks(inv?.remarks || '');
			})
			.finally(() => setLoading(false));
	}, [dispatch, id]);

	const subtotal = Number(invoice?.subtotal || 0);
	const gst = useMemo(() => {
		const explicit =
			Number(invoice?.cgst_amount || 0) +
			Number(invoice?.sgst_amount || 0) +
			Number(invoice?.igst_amount || 0);
		return explicit || subtotal * GST_RATE;
	}, [invoice, subtotal]);
	const grandTotal = subtotal + gst - Number(tds || 0) - Number(debit || 0);

	const handleSubmit = () => {
		setSubmitting(true);
		dispatch(
			editRegeneratedClientInvoiceV2({
				req_data: {
					client_invoice_id: Number(id),
					tds_amount: Number(tds || 0),
					customer_debit_amount: Number(debit || 0),
					remarks: remarks || undefined,
				},
			})
		)
			.then((res) => {
				if (res?.status === 'success') {
					message.success('Regeneration submitted for approval.');
					navigate(LIST_ROUTE);
				} else {
					setSubmitting(false);
				}
			})
			.catch(() => setSubmitting(false));
	};

	return (
		<Box>
			<Flex
				alignItems="center"
				style={{
					background: '#fff',
					padding: '16px 24px',
					margin: '-24px -24px 24px -24px',
					borderBottom: '1px solid #f0f0f0',
					gap: 12,
				}}
			>
				<ArrowLeftOutlined
					style={{ cursor: 'pointer' }}
					onClick={() => navigate(LIST_ROUTE)}
				/>
				<Text style={{ fontSize: 18, fontWeight: 600 }}>
					Client Invoice Editing{invoice?.invoice_no ? ` : ${invoice.invoice_no}` : ''}
				</Text>
			</Flex>

			<Spin spinning={loading}>
				{invoice && (
					<>
						<Card>
							<Row gutter={16}>
								<Col lg={8}>Invoice No : {invoice.invoice_no || '-'}</Col>
								<Col lg={8}>Client : {invoice.client || '-'}</Col>
								<Col lg={8}>Revision : {invoice.version ?? '-'}</Col>
							</Row>
							<Row gutter={16} style={{ marginTop: 12 }}>
								<Col lg={8}>Billing Period : {invoice.billing_period || '-'}</Col>
								<Col lg={8}>No. of Trips : {invoice.no_of_trips ?? '-'}</Col>
								<Col lg={8}>Status : {invoice.invoice_status || '-'}</Col>
							</Row>
						</Card>

						{trips.regular?.length > 0 && (
							<Card>
								<SubTitle>Regular Trip Details</SubTitle>
								<AntdTable
									rowKey={(r, i) => `reg-${r.trip_id || i}`}
									columns={TRIP_COLUMNS}
									dataSource={trips.regular}
									pagination={false}
								/>
							</Card>
						)}

						{trips.adhoc?.length > 0 && (
							<Card>
								<SubTitle>Adhoc Trip Details</SubTitle>
								<AntdTable
									rowKey={(r, i) => `adh-${r.trip_id || i}`}
									columns={TRIP_COLUMNS}
									dataSource={trips.adhoc}
									pagination={false}
								/>
							</Card>
						)}

						<Card style={{ maxWidth: 560 }}>
							<SubTitle>Total Payable</SubTitle>
							<GreyLine gutter={16} justify="space-between">
								<Col lg={12}>Trip Subtotal :</Col>
								<Col lg={12}>{formatINR(subtotal)}</Col>
							</GreyLine>
							<RowLine gutter={16} justify="space-between">
								<Col lg={12}>GST (18%) :</Col>
								<Col lg={12}>{formatINR(gst)}</Col>
							</RowLine>
							<RowLine gutter={16} justify="space-between">
								<Col lg={12}>TDS Amount :</Col>
								<Col lg={12}>
									<InputNumber
										style={{ width: '100%' }}
										min={0}
										value={tds}
										onChange={(v) => setTds(v ?? 0)}
									/>
								</Col>
							</RowLine>
							<RowLine gutter={16} justify="space-between">
								<Col lg={12}>Customer Debit :</Col>
								<Col lg={12}>
									<InputNumber
										style={{ width: '100%' }}
										min={0}
										value={debit}
										onChange={(v) => setDebit(v ?? 0)}
									/>
								</Col>
							</RowLine>
							<GreyLine gutter={16} justify="space-between">
								<Col lg={12}>
									<b>Grand Total :</b>
								</Col>
								<Col lg={12}>
									<b>{formatINR(grandTotal)}</b>
								</Col>
							</GreyLine>
						</Card>

						<Card>
							<SubTitle>Remarks</SubTitle>
							<TextArea
								rows={3}
								value={remarks}
								maxLength={1024}
								onChange={(e) => setRemarks(e.target.value)}
								placeholder="Optional remarks…"
							/>
						</Card>

						<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
							<CancelButton type="button" onClick={() => navigate(LIST_ROUTE)}>
								Cancel
							</CancelButton>
							<Button
								borderRadius="4px"
								backgroundColor="#18405d"
								type="button"
								onClick={handleSubmit}
								disabled={submitting}
							>
								Submit
							</Button>
						</Box>
					</>
				)}
			</Spin>
		</Box>
	);
}

export default EditClientInvoicePageV2;
